import React, { useEffect, useRef, useState } from "react";
import useGetLanguage from "./useGetLanguage";
import { LanguageText } from "../dictionary/UtilsText";

interface DateRangePickerProps {
  startDate: Date;
  endDate: Date;
  onChange: (startDate: Date, endDate: Date) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
}) => {
  const language: LanguageText = useGetLanguage({ fileName: "UtilsText" });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectOptions = [
    language?.components?.dateRangePicker?.selectOptions?.thisMonth || "This Month",
    language?.components?.dateRangePicker?.selectOptions?.lastMonth || "Last Month",
  ];

  useEffect(() => {
    if (startDate > endDate) {
      setErrorMessage(
        language?.components?.dateRangePicker?.errorMsg ||
          "Start date cannot be after end date"
      );
    } else {
      setErrorMessage(null);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDateInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = new Date(e.target.value);
    onChange(newStartDate, endDate);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = new Date(e.target.value);
    onChange(startDate, newEndDate);
  };

  const setDateRange = (range: string) => {
    const today = new Date();
    let newStart: Date, newEnd: Date;

    if (range === selectOptions[0]) {
      newStart = new Date(today.getFullYear(), today.getMonth(), 1);
      newEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (range === selectOptions[1]) {
      newStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      newEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    } else {
      return;
    }

    onChange(newStart, newEnd);
    setIsDropdownOpen(false);
  };

  return (
    <div className="flex flex-col items-center space-y-4 mt-6 mb-6 p-4 bg-white rounded-lg shadow-md">
      <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 w-full">
        <div className="flex flex-col relative w-full m-2 sm:w-40">
          <input
            type="date"
            max={formatDateInput(endDate)}
            value={formatDateInput(startDate)}
            onChange={handleStartDateChange}
            className="px-2 py-1 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          <label className="absolute left-2 top-1 text-xs font-medium text-gray-500 transform -translate-y-6 origin-[0] scale-75">
            {language?.components?.dateRangePicker?.from || "From"}
          </label>
        </div>
        <div className="flex flex-col relative w-full sm:w-40">
          <input
            type="date"
            min={formatDateInput(startDate)}
            value={formatDateInput(endDate)}
            onChange={handleEndDateChange}
            className="px-2 py-1 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          <label className="absolute left-2 top-1 text-xs font-medium text-gray-500 transform -translate-y-6 origin-[0] scale-75">
            {language?.components?.dateRangePicker?.to || "To"}
          </label>
        </div>
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            ▼
          </button>
          {isDropdownOpen && (
            <div className="absolute z-50 mt-1 w-48 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto sm:right-0 left-1/2 sm:left-auto transform sm:translate-x-0 translate-x-[-50%]">
              <ul className="py-1">
                {selectOptions.map((option) => (
                  <li
                    key={option}
                    onClick={() => setDateRange(option)}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-left w-full"
                  >
                    {option}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      {errorMessage && <div className="text-red-500 text-sm mt-2">{errorMessage}</div>}
    </div>
  );
};

export default DateRangePicker;
